import { useCallback, useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { Modal, showSnackbar } from 'fluxo-ui';

import { ExamEditor, ExamPreview, InviteManager } from '../components/exam';
import examService from '../services/exam.service';
import { useExamStore } from '../store/examStore';

const ExamManagementPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { currentExam, loading, fetchExam, createExam, updateExam, setCurrentExam } = useExamStore();
    const [showPreview, setShowPreview] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const [invites, setInvites] = useState<any[]>([]);

    const isEditMode = !!id;

    const fetchInvites = useCallback(async () => {
        if (!id) return;
        try {
            const response = await examService.getInvites(id, { limit: 200 });
            setInvites(
                (response.invites || []).map((invite: any) => ({
                    id: invite._id,
                    email: invite.email,
                    name: invite.name,
                    status: invite.status,
                    sentAt: invite.sentAt,
                    reminderCount: invite.reminderCount ?? 0,
                })),
            );
        } catch {
            // Invites are non-critical for the editor; ignore load failures
        }
    }, [id]);

    useEffect(() => {
        if (isEditMode) {
            fetchInvites();
        }
    }, [isEditMode, fetchInvites]);

    const handleSendInvites = async (entries: Array<{ name: string; email: string }>) => {
        if (!id) return;
        const result = await examService.sendInvites(id, entries);
        if (result.failed > 0) {
            const reasons = result.errors
                .slice(0, 3)
                .map((e) => `${e.email}: ${e.reason}`)
                .join('; ');
            showSnackbar(`${result.successful} sent, ${result.failed} failed. ${reasons}`, 'Invites', { type: 'warning', timeout: 8000 });
        } else {
            showSnackbar(`${result.successful} invitation(s) sent`, 'Invites', { type: 'success' });
        }
        fetchInvites();
    };

    const handleResendInvite = async (inviteId: string) => {
        if (!id) return;
        await examService.resendInvite(id, inviteId);
        fetchInvites();
    };

    const handleRemoveInvite = async (inviteId: string) => {
        if (!id) return;
        await examService.deleteInvite(id, inviteId);
        fetchInvites();
    };

    useEffect(() => {
        const loadExam = async () => {
            if (isEditMode && id) {
                try {
                    await fetchExam(id);
                } catch (error: any) {
                    showSnackbar(error.message || 'Failed to load exam', 'Error', { type: 'error' });
                    navigate('/exams');
                    return;
                }
            } else {
                setCurrentExam(null);
            }
            setInitialized(true);
        };
        loadExam();

        return () => {
            setCurrentExam(null);
        };
    }, [id, isEditMode, fetchExam, setCurrentExam, navigate]);

    const transformExamData = (examData: any) => {
        const { settings = {}, questions = [], category, ...rest } = examData;

        // Extract and properly nest settings fields for the backend API
        const {
            // Proctoring fields
            fullscreenRequired,
            trackWindowFocus,
            disableCopyPaste,
            webcamProctoring,
            // Grading fields
            gradingScale,
            passingScore,
            negativeMarking,
            negativeMarkingValue,
            // Feedback fields
            showScoreAfterSubmission,
            showCorrectAnswersAfter,
            allowExamReview,
            // Top-level fields from settings
            duration,
            startDateTime,
            endDateTime,
            timezone,
            accessControl,
            passcode,
            emailWhitelist,
            maxAttempts,
            retakeCooldownMinutes,
            attemptScoringPolicy,
            allowBackwardNavigation,
            questionAccessMode,
            allowBookmarking,
            showQuestionPalette,
            autoSaveIntervalSeconds,
        } = settings;

        // Strip extra fields from questions (title, type are not expected by backend schema)
        const cleanQuestions = questions.map((q: any) => ({
            questionId: q.questionId,
            marks: q.marks,
            timeSeconds: q.timeSeconds,
            shuffleOptions: q.shuffleOptions,
            order: q.order,
        }));

        return {
            ...rest,
            // Only include category if it's a valid ObjectId (24-char hex string)
            ...(category && /^[0-9a-fA-F]{24}$/.test(category) ? { category } : {}),
            questions: cleanQuestions,
            duration,
            startDateTime: startDateTime || undefined,
            endDateTime: endDateTime || undefined,
            timeZone: timezone,
            accessControl,
            passcode,
            emailWhitelist,
            maxAttempts,
            retakeCooldownMinutes,
            attemptScoringPolicy,
            allowBackwardNavigation,
            questionAccessMode,
            allowBookmarking,
            showQuestionPalette,
            autoSaveIntervalSeconds,
            proctoring: {
                fullscreenRequired: fullscreenRequired ?? false,
                trackWindowFocus: trackWindowFocus ?? false,
                disableCopyPaste: disableCopyPaste ?? false,
                webcamProctoring: webcamProctoring ?? false,
            },
            grading: {
                gradingScale: gradingScale ?? 'percentage',
                passingScore: passingScore ?? 60,
                negativeMarking: negativeMarking ?? false,
                ...(negativeMarkingValue != null ? { negativeMarkingValue } : {}),
            },
            feedback: {
                showScoreAfterSubmission: showScoreAfterSubmission ?? 'immediately',
                showCorrectAnswersAfter: showCorrectAnswersAfter ?? 'after_deadline',
                allowExamReview: allowExamReview ?? 'full_review',
            },
        };
    };

    const handleSave = async (examData: any, publish?: boolean) => {
        try {
            const apiData = transformExamData(examData);
            let examId = id;
            if (isEditMode && id) {
                await updateExam(id, apiData);
            } else {
                const exam = await createExam(apiData);
                examId = exam._id;
                if (!publish) {
                    showSnackbar('Exam created successfully', 'Success', { type: 'success' });
                    navigate(`/exams/${exam._id}/edit`);
                    return;
                }
            }
            if (publish && examId) {
                await examService.publishExam(examId);
                showSnackbar('Exam published successfully', 'Success', { type: 'success' });
            } else {
                showSnackbar('Exam saved successfully', 'Success', { type: 'success' });
            }
            navigate('/exams');
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to save exam', 'Error', { type: 'error' });
        }
    };

    const handleCancel = () => {
        navigate('/exams');
    };

    if (!initialized || (isEditMode && loading && !currentExam)) {
        return (
            <div className="p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
                    <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded"></div>
                </div>
            </div>
        );
    }

    const examData = currentExam
        ? {
              id: currentExam._id,
              title: currentExam.title,
              description: currentExam.description || '',
              type: currentExam.type,
              category: (currentExam as any).category?._id || (currentExam as any).category || '',
              instructions: (currentExam as any).instructions || '',
              questions: (currentExam.questions || []).map((q: any) => ({
                  questionId: q.questionId?._id || q.questionId,
                  title: q.questionId?.title || q.title || 'Untitled',
                  type: q.questionId?.type || q.type || '',
                  marks: q.marks,
                  timeSeconds: q.timeSeconds,
                  shuffleOptions: q.shuffleOptions,
                  order: q.order,
              })),
              settings: {
                  duration: currentExam.duration,
                  startDateTime: (currentExam as any).startDateTime || '',
                  endDateTime: (currentExam as any).endDateTime || '',
                  accessControl: (currentExam as any).accessControl || 'open_access',
                  passcode: (currentExam as any).passcode || '',
                  maxAttempts: (currentExam as any).maxAttempts || 1,
                  attemptScoringPolicy: (currentExam as any).attemptScoringPolicy || 'highest',
                  allowBackwardNavigation: (currentExam as any).allowBackwardNavigation ?? true,
                  questionAccessMode: (currentExam as any).questionAccessMode || 'random_access',
                  allowBookmarking: (currentExam as any).allowBookmarking ?? true,
                  showQuestionPalette: (currentExam as any).showQuestionPalette ?? true,
                  autoSaveIntervalSeconds: (currentExam as any).autoSaveIntervalSeconds || 30,
                  ...((currentExam as any).proctoring || {}),
                  ...((currentExam as any).grading || {}),
                  ...((currentExam as any).feedback || {}),
              },
          }
        : undefined;

    return (
        <div className="p-6 space-y-6">
            <ExamEditor
                exam={examData}
                onSave={handleSave}
                onCancel={handleCancel}
                onPreview={() => setShowPreview(true)}
                loading={loading}
            />

            {isEditMode && (
                <InviteManager
                    examId={id}
                    invites={invites}
                    onInvite={handleSendInvites}
                    onResend={handleResendInvite}
                    onRemove={handleRemoveInvite}
                />
            )}

            <Modal isOpen={showPreview} onClose={() => setShowPreview(false)} title="Exam Preview" size="xl">
                {examData && <ExamPreview exam={examData} onClose={() => setShowPreview(false)} />}
            </Modal>
        </div>
    );
};

export default ExamManagementPage;
